import Chip from '@mui/material/Chip';
import {
	blue,
	green,
	orange,
	purple,
	red,
	deepOrange
} from '@mui/material/colors';
import type { SxProps, Theme } from '@mui/material/styles';
import type { CSSProperties, ComponentType } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AcceptedIcon } from '@/assets/icons/AcceptedIcon';
import { PartiallyAcceptedIcon } from '@/assets/icons/PartiallyAcceptedIcon';
import { PendingReviewIcon } from '@/assets/icons/PendingReviewIcon';
import { ProcessingIcon } from '@/assets/icons/ProcessingIcon';
import { RejectedIcon } from '@/assets/icons/RejectedIcon';
import { chipsStyles as sx } from '@/components/Chips/Chips.styles';
import { DcrFieldResultStatus } from '@/core/graphql/generated/types';

import { StatusChipTooltip } from './StatusChipTooltip';

export type StatusKind =
	'accepted' | 'rejected' | 'pending' | 'processing' | 'partial';

type StatusColors = Pick<
	CSSProperties,
	'borderColor' | 'backgroundColor' | 'color'
>;

type StatusConfig = {
	icon: ComponentType<{ sx?: SxProps<Theme> }>;
	label: string;
	colors: StatusColors;
	withTooltipWhenRequested: boolean;
};

export const statusChipColors: Record<StatusKind, StatusColors> = {
	accepted: {
		borderColor: green[100],
		backgroundColor: green[50],
		color: green[800]
	},
	rejected: {
		borderColor: red[100],
		backgroundColor: red[50],
		color: 'error.main'
	},
	pending: {
		borderColor: orange[100],
		backgroundColor: orange[50],
		color: deepOrange[800]
	},
	processing: {
		borderColor: purple[100],
		backgroundColor: purple[50],
		color: purple[800]
	},
	partial: {
		borderColor: blue[100],
		backgroundColor: blue[50],
		color: blue[800]
	}
};

const useStatusConfig = (): Record<StatusKind, StatusConfig> => {
	const { t } = useTranslation();

	return useMemo(
		() => ({
			accepted: {
				icon: AcceptedIcon,
				label: t('Accepted'),
				colors: statusChipColors.accepted,
				withTooltipWhenRequested: true
			},
			rejected: {
				icon: RejectedIcon,
				label: t('Rejected'),
				colors: statusChipColors.rejected,
				withTooltipWhenRequested: false
			},
			pending: {
				icon: PendingReviewIcon,
				label: t('Pending Review'),
				colors: statusChipColors.pending,
				withTooltipWhenRequested: false
			},
			processing: {
				icon: ProcessingIcon,
				label: t('Processing'),
				colors: statusChipColors.processing,
				withTooltipWhenRequested: false
			},
			partial: {
				icon: PartiallyAcceptedIcon,
				label: t('Partially Accepted'),
				colors: statusChipColors.partial,
				withTooltipWhenRequested: true
			}
		}),
		[t]
	);
};

const resolveKind = (
	status: DcrFieldResultStatus | StatusKind | null
): StatusKind => {
	switch (status) {
		case DcrFieldResultStatus.ChangeRejected:
			return 'rejected';
		// ChangeAlreadyapplied and ChangeAdded are defined but not possible to be returned by the API
		case DcrFieldResultStatus.ChangeAccepted:
		case DcrFieldResultStatus.ChangeAlreadyapplied:
		case DcrFieldResultStatus.ChangeAdded:
			return 'accepted';
		case DcrFieldResultStatus.ChangeModified:
			return 'partial';
		case null:
			return 'pending';
		default:
			return status;
	}
};

type Props = {
	status: DcrFieldResultStatus | StatusKind | null;
	showHelp?: boolean;
};

export const StatusChip = ({ status, showHelp = false }: Props) => {
	const config = useStatusConfig();
	const kind = resolveKind(status);
	const { icon: Icon, label, colors, withTooltipWhenRequested } = config[kind];

	const chip = (
		<Chip
			icon={<Icon sx={sx.dcrStatusChipIcon} />}
			label={label}
			variant='outlined'
			sx={[sx.dcrStatusChip, colors]}
		/>
	);

	if (showHelp && withTooltipWhenRequested) {
		return <StatusChipTooltip>{chip}</StatusChipTooltip>;
	}

	return chip;
};
